// pages/stock/StockWACHistory.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, CircularProgress, Typography, Box, Chip, Stack,
    IconButton, TablePagination, Grid, Tooltip
} from '@mui/material';
import {
    Close as CloseIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    SwapHoriz as SwapHorizIcon,
    Warehouse as WarehouseIcon,
    Build as BuildIcon
} from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { stockAPI } from '../../services/api';

const reasonConfig = {
    TRANSFER_FROM_CENTRAL: {
        color: 'primary',
        icon: <WarehouseIcon fontSize="small" />,
        label: 'From Central'
    },
    TRANSFER_FROM_BRANCH: {
        color: 'info',
        icon: <SwapHorizIcon fontSize="small" />,
        label: 'From Branch'
    },
    MANUAL_ADJUSTMENT: {
        color: 'warning',
        icon: <BuildIcon fontSize="small" />,
        label: 'Manual Adjustment'
    }
};

const StockWACHistory = ({ open, onClose, stockItem, branchId, branchName }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [wacData, setWacData] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);

    const fetchWACHistory = useCallback(async () => {
        if (!stockItem?._id || !branchId) return;

        setLoading(true);
        try {
            const params = {
                page: page + 1,
                limit: rowsPerPage,
            };
            if (dateFrom) params.dateFrom = dateFrom.toISOString();
            // end of day for dateTo to include the entire day
            if (dateTo) {
                dateTo.setHours(23, 59, 59, 999);
                params.dateTo = dateTo.toISOString();
            }

            // branchId is passed as second arg, gets sent as query param
            const response = await stockAPI.getBranchWACHistory(
                stockItem._id,
                branchId,
                params
            );
            // Handle the response structure: { status, data, message }
            setWacData(response.data?.data || response.data);
        } catch (error) {
            toast.error(
                error.response?.data?.message ||
                t('stock.wacHistoryFetchError', 'Failed to fetch WAC history')
            );
        } finally {
            setLoading(false);
        }
    }, [stockItem, branchId, page, rowsPerPage, dateFrom, dateTo, t]);
    useEffect(() => {
        if (open) {
            fetchWACHistory();
        }
    }, [open, fetchWACHistory]);

    const handleClearDates = () => {
        setDateFrom(null);
        setDateTo(null);
        setPage(0);
    };

    const history = wacData?.history || [];
    const pagination = wacData?.pagination || { total: 0, pages: 1 };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h6">
                            {t('stock.wacHistory', 'Weighted Average Cost History')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {stockItem?.name} — {branchName || branchId}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                {/* Summary */}
                {wacData && (
                    <Stack direction="row" spacing={2} mb={2}>
                        <Chip
                            label={`${t('stock.currentWac', 'Current WAC')}: ${(wacData.currentWac ?? 0).toFixed(2)}`}
                            color="primary"
                        />
                        <Chip
                            label={`${t('stock.currentQuantity', 'Current Qty')}: ${wacData.currentQuantity ?? 0}`}
                            color="info"
                            variant="outlined"
                        />
                        <Chip
                            label={`${t('stock.totalEntries', 'Total Entries')}: ${pagination.total}`}
                            variant="outlined"
                        />
                    </Stack>
                )}

                {/* Date Range Filter */}
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label={t('common.dateFrom', 'From')}
                                    value={dateFrom}
                                    onChange={(date) => { setDateFrom(date); setPage(0); }}
                                    slotProps={{
                                        textField: { fullWidth: true, size: 'small' }
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label={t('common.dateTo', 'To')}
                                    value={dateTo}
                                    onChange={(date) => { setDateTo(date); setPage(0); }}
                                    slotProps={{
                                        textField: { fullWidth: true, size: 'small' }
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            {(dateFrom || dateTo) && (
                                <Button variant="outlined" size="small" onClick={handleClearDates}>
                                    {t('common.clearFilters', 'Clear Dates')}
                                </Button>
                            )}
                        </Grid>
                    </Grid>
                </Paper>

                {/* WAC History Table */}
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow >
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    {t('stock.date', 'Date')}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    {t('stock.reason', 'Reason')}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    {t('stock.source', 'Source')}
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                    {t('stock.transferQuantity', 'Transfer Qty')}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                    {t('stock.sourceWac', 'Source WAC')}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                    {t('stock.newAverageCost', 'New WAC')}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    {t('stock.triggeredBy', 'By')}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : history.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">
                                            {t('stock.noWacHistory', 'No WAC history found')}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                history.map((entry, index) => {
                                    const config = reasonConfig[entry.reason] || {
                                        color: 'default',
                                        icon: null,
                                        label: entry.reason
                                    };

                                    // Check WAC direction compared to previous entry
                                    const prevEntry = index < history.length - 1 ? history[index + 1] : null;
                                    const wacDirection = prevEntry
                                        ? entry.averageCost > prevEntry.averageCost ? 'up'
                                            : entry.averageCost < prevEntry.averageCost ? 'down'
                                                : 'same'
                                        : 'same';

                                    const sourceName = entry.sourceBranchId
                                        ? (entry.sourceBranchId?.name || entry.sourceBranchId)
                                        : t('stock.centralStock', 'Central Stock');

                                    return (
                                        <TableRow
                                            key={entry._id || index}
                                            hover
                                            sx={{
                                                // backgroundColor: index % 2 ? 'action.hover' : 'background.paper'
                                            }}
                                        >
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {new Date(entry.effectiveFrom).toLocaleString()}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={config.icon}
                                                    label={t(`stock.wacReason.${entry.reason}`, config.label)}
                                                    color={config.color}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {sourceName}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={`+${entry.transferQuantity}`}
                                                    size="small"
                                                    color="success"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body2">
                                                    {(entry.sourceWac ?? 0).toFixed(2)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight="bold"
                                                        color={
                                                            wacDirection === 'up' ? 'error.main'
                                                                : wacDirection === 'down' ? 'success.main'
                                                                    : 'text.primary'
                                                        }
                                                    >
                                                        {(entry.averageCost ?? 0).toFixed(2)}
                                                    </Typography>
                                                    {wacDirection === 'up' && (
                                                        <Tooltip title={t('stock.wacIncreased', 'WAC Increased')}>
                                                            <TrendingUpIcon fontSize="small" color="error" />
                                                        </Tooltip>
                                                    )}
                                                    {wacDirection === 'down' && (
                                                        <Tooltip title={t('stock.wacDecreased', 'WAC Decreased')}>
                                                            <TrendingDownIcon fontSize="small" color="success" />
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {entry.triggeredBy?.username || entry.triggeredBy || '—'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                {pagination.total > 0 && (
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={pagination.total}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                    />
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>{t('common.close', 'Close')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default StockWACHistory;